import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { Stack, Typography } from "@mui/material";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../../../pages/Home/constants/homeGlass";
import { getClientName } from "../../../../core/utils/clientDisplay";
import { parseThemeConfig } from "../../../../core/utils/themeConfig";
import { MODAL_HEIGHT_SM } from "../../../layoutMetrics";

export const ACCENT = "#6366F1";
export const ACCENT_SECONDARY = "#8B5CF6";
export const THEME_ACCENT = "#D4A017";
export const THEME_PANEL = "#1A1A1A";
export const THEME_BORDER = "rgba(255,255,255,0.08)";
export const DARK_BORDER = "rgba(255,255,255,0.1)";

const SIDEBAR_GAP = "14px";
export const MODAL_LEFT_INSET = `calc(35px + 230px + ${SIDEBAR_GAP})`;
export const MODAL_RIGHT_MARGIN = "8px";
export const MODAL_VERTICAL_INSET_SM = "20px";
export const MODAL_WIDTH = `min(960px, calc(100vw - 35px - 230px - ${SIDEBAR_GAP} - ${MODAL_RIGHT_MARGIN}))`;
export const MODAL_HEIGHT = MODAL_HEIGHT_SM;

const ROLE_SELECT_HEIGHT = 38;
const USER_PICKER_HEIGHT = 38;

export const WIZARD_STEPS = [
  { id: "details", label: "Client Details", shortLabel: "Details", icon: PersonOutlineIcon },
  { id: "assets", label: "Upload Assets", shortLabel: "Assets", icon: CloudUploadOutlinedIcon },
  { id: "users", label: "Assign Users", shortLabel: "Users", icon: GroupsOutlinedIcon },
  { id: "theme", label: "Theme Selection", shortLabel: "Theme", icon: PaletteOutlinedIcon },
];

export const CLIENT_STEP = {
  DETAILS: 0,
  ASSETS: 1,
  USERS: 2,
  THEME: 3,
};

export const EMPTY_FORM = {
  clientName: "",
  domainName: "",
  domainId: null,
  knowledgeBank: [],
  logo: [],
  theme: [],
  themeConfig: [],
  meeting: [],
  otherDocuments: [],
};

export const EMPTY_EXISTING_RESOURCES = {
  knowledgeBank: [],
  logo: [],
  meeting: [],
  otherDocuments: [],
  theme: [],
};

export const isImageFileName = (name = "") =>
  /\.(png|jpe?g|webp|bmp|svg)$/i.test(String(name));

export const textFieldSx = {
  input: {
    color: "#f8fafc",
    fontSize: 14,
    "&::placeholder": { color: "rgba(234, 231, 226, 0.52)", opacity: 1 },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "rgba(255,255,255,0.07)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&:hover fieldset": { borderColor: `${THEME_ACCENT}99` },
    "&.Mui-focused fieldset": {
      borderColor: THEME_ACCENT,
      boxShadow: `0 0 0 2px ${THEME_ACCENT}1f`,
    },
  },
};

export const stepContentCardSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  background:
    "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
  overflow: "hidden",
};

export const stepMotionVariants = {
  enter: { opacity: 0, x: 16 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
};

export const selectMenuPaperSx = {
  bgcolor: "rgba(28, 29, 22, 0.96)",
  backdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  color: "#fff",
  "& .MuiMenuItem-root": {
    fontSize: 14,
    "&.Mui-selected": { bgcolor: `${THEME_ACCENT}33` },
    "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
  },
};

export const userTableColSx = { width: "34%", minWidth: 0, maxWidth: "34%" };
export const rolesTableColSx = { minWidth: 0, width: "auto", overflow: "hidden" };
export const roleSelectWrapperSx = {
  minWidth: 0,
  maxWidth: "100%",
  width: "100%",
  overflow: "hidden",
};

export const roleSelectSx = {
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  borderRadius: "10px",
  fontSize: 13,
  color: "#f8fafc",
  background: "rgba(255,255,255,0.07)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${THEME_ACCENT}99` },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: THEME_ACCENT,
    borderWidth: 1,
  },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    height: ROLE_SELECT_HEIGHT,
    maxHeight: ROLE_SELECT_HEIGHT,
    alignItems: "center",
    overflow: "hidden",
  },
  "& .MuiSelect-select": {
    width: "100% !important",
    maxWidth: "100% !important",
    height: `${ROLE_SELECT_HEIGHT - 2}px !important`,
    minHeight: "unset !important",
    maxHeight: `${ROLE_SELECT_HEIGHT - 2}px !important`,
    display: "flex !important",
    alignItems: "center",
    py: "0 !important",
    pr: "34px !important",
    overflow: "hidden !important",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
};

export const roleChipsRowSx = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 0.5,
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: "100%",
  width: 0,
  overflowX: "auto",
  overflowY: "hidden",
  height: 22,
  maxHeight: 22,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

export const roleChipSx = {
  height: 22,
  maxHeight: 22,
  fontSize: 11,
  fontWeight: 600,
  flexShrink: 0,
  bgcolor: `${THEME_ACCENT}22`,
  color: THEME_ACCENT,
  border: `1px solid ${THEME_ACCENT}44`,
  "& .MuiChip-label": { px: 0.75, py: 0, lineHeight: 1.2 },
  "& .MuiChip-deleteIcon": {
    color: `${THEME_ACCENT}aa`,
    fontSize: 14,
    margin: "0 2px 0 -2px",
    "&:hover": { color: THEME_ACCENT },
  },
};

export const userPickerValueSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
  flex: "0 1 auto",
  maxWidth: "calc(100% - 72px)",
  overflow: "hidden",
  height: 24,
  mr: 0.5,
  pointerEvents: "none",
};

export const userPickerNamesRowSx = {
  minWidth: 0,
  flex: "0 1 auto",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
  whiteSpace: "nowrap",
  fontSize: 13,
  fontWeight: 500,
  color: "#f8fafc",
  lineHeight: 1.2,
};

export const userAutocompleteSx = {
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "nowrap !important",
    alignItems: "center",
    height: USER_PICKER_HEIGHT,
    maxHeight: USER_PICKER_HEIGHT,
    overflow: "hidden",
    py: "0 !important",
    pr: "9px !important",
    cursor: "text",
  },
  "& .MuiAutocomplete-input": {
    minWidth: "0 !important",
    width: "0 !important",
    flex: "1 1 auto !important",
    py: "0 !important",
    px: "4px !important",
  },
};

export const userPickerTextFieldSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    height: USER_PICKER_HEIGHT,
    maxHeight: USER_PICKER_HEIGHT,
    overflow: "hidden",
  },
};

export const glassPanelSx = {
  borderRadius: "12px",
  background: homeGlassHero.rest,
  backdropFilter: HOME_GLASS_BACKDROP,
  WebkitBackdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 32px rgba(0,0,0,0.18)",
};

export const buildClientFormFromTarget = (client) => {
  if (!client) return EMPTY_FORM;
  return {
    clientName: getClientName(client) || client.clientName || "",
    domainName:
      client.domainName ||
      client.DomainName ||
      client.domain_name ||
      "",
    domainId:
      client.domainId ??
      client.domain_id ??
      client.clientDomainId ??
      client.DomainId ??
      null,
    knowledgeBank: [],
    logo: [],
    theme: [],
    themeConfig: parseThemeConfig(client.themeConfig ?? client.theme_config),
    meeting: [],
    otherDocuments: [],
  };
};

export const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

export const normalizeApiUser = (user) => {
  if (!user) return null;
  const uid = user.id ?? user.uid ?? user.userId ?? user.sub ?? "";
  if (!uid) return null;
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const name =
    user.name || `${firstName} ${lastName}`.trim() || user.username || "";
  return {
    uid,
    id: uid,
    uname: user.username || user.uname || user.preferred_username || "",
    name,
    firstName,
    lastName,
    email: user.email || "",
    enabled: user.enabled,
    raw: user,
  };
};

export const getUserLabel = (user) => {
  if (!user) return "";
  return user.name || user.uname || user.email || "User";
};

export const getUserHandle = (user) => {
  const handle = user?.uname || user?.username || "";
  return handle ? `@${handle.replace(/^@/, "")}` : "";
};

export const getUserUsername = (user) =>
  user?.uname || user?.username || user?.preferred_username || "";

export const isCreatorUser = (user, creatorUser) => {
  if (!creatorUser || !user) return false;
  return (
    String(user.uid) === String(creatorUser.id) ||
    getUserUsername(user) === creatorUser.username ||
    (creatorUser.email && user.email === creatorUser.email)
  );
};

export const parseClientUsersRoleMap = (users) => {
  const map = new Map();
  const setRoles = (username, roles) => {
    const key = String(username ?? "").trim().toLowerCase();
    if (!key) return;
    const roleIds = (Array.isArray(roles) ? roles : [])
      .map((roleId) => Number(roleId))
      .filter((id) => Number.isFinite(id));
    const existing = map.get(key) || [];
    map.set(key, [...new Set([...existing, ...roleIds])]);
  };

  if (Array.isArray(users)) {
    users.forEach((item) => {
      if (typeof item === "string") {
        setRoles(item, []);
        return;
      }
      if (!item || typeof item !== "object") return;
      const username =
        item.username ?? item.userName ?? item.uname ?? item.preferred_username;
      const roles = item.roles ?? item.roleIds ?? item.clientRoles ?? [];
      setRoles(username, roles);
    });
    return map;
  }

  if (users && typeof users === "object") {
    Object.entries(users).forEach(([username, roles]) => setRoles(username, roles));
  }
  return map;
};

export const mergeFiles = (existing, incoming) => {
  const keys = new Set((existing || []).map(fileKey));
  const next = [...(existing || [])];
  incoming.forEach((file) => {
    const key = fileKey(file);
    if (keys.has(key)) return;
    keys.add(key);
    next.push(file);
  });
  return next;
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb < 10 ? kb.toFixed(0) : kb.toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const isImageFile = (file) =>
  file?.type?.startsWith("image/") ||
  /\.(png|jpe?g|webp|bmp|svg)$/i.test(file?.name || "");

export const getFileAccentColor = (file, fallback) => {
  const name = (file?.name || "").toLowerCase();
  if (/\.(xlsx?|csv)$/.test(name)) return "#22C55E";
  if (/\.pdf$/.test(name)) return "#EF4444";
  if (/\.(ai|esp|eps)$/.test(name) || isImageFile(file)) return "#A855F7";
  if (/\.(mp4|mov|webm|mkv)$/.test(name)) return ACCENT;
  return fallback;
};

export {
  BG_FIT_OPTIONS,
  getBackgroundStyles,
} from "../../../../core/utils/themeConfig";

export function StepSectionTitle({ icon: Icon, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Icon sx={{ fontSize: 17, color: "rgba(255,255,255,0.55)" }} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.02 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: 0.6 }}>
      {children}
    </Typography>
  );
}
